import { formatDecimal } from "@aclymatepackages/formatters";

const confidenceTier = (confidence) => {
  if (confidence >= 0.9) {
    return "high";
  }
  if (confidence >= 0.75) {
    return "medium";
  }
  return "low";
};

const TIER_LABEL_MAP = { high: "High", medium: "Medium", low: "Low" };

const MATCH_LABEL_MAP = {
  high: "Strong match",
  medium: "Likely match",
  low: "Weak match"
};

const IMPACT_LABEL_MAP = {
  high: "High impact",
  medium: "Medium impact",
  low: "Low impact"
};

const TIER_CHIP_COLOR_MAP = {
  high: "success",
  medium: "warning",
  low: "default"
};

const IMPACT_CHIP_COLOR_MAP = {
  high: "error",
  medium: "warning",
  low: "default"
};

const confidenceLabel = (confidence) => TIER_LABEL_MAP[confidenceTier(confidence)];

const confidenceChipColor = (confidence) =>
  TIER_CHIP_COLOR_MAP[confidenceTier(confidence)];

// Acronym/subsequence matches are a structural fact (an initials or
// contiguous-token relationship), not a probabilistic string-similarity
// score -- MATCH_LABEL_MAP's "Strong/Likely/Weak match" tiering describes
// fuzzy-score confidence and doesn't apply here. Falls back to null so
// callers use the numeric-confidence label for "fuzzy"/"exact" clusters.
const MATCH_BASIS_LABEL_MAP = {
  acronym: "Acronym match",
  subsequence: "Subsequence match"
};

const matchBasisLabel = (matchBasis) => MATCH_BASIS_LABEL_MAP[matchBasis] || null;

const REASON_LABEL_MAP = {
  "low-confidence": "Low confidence",
  "merge-conflict": "Merge conflict",
  "government-entity": "Tax/regulatory payment"
};

const REASON_CHIP_COLOR_MAP = {
  "low-confidence": "warning",
  "merge-conflict": "info",
  "government-entity": "info"
};

const reasonLabel = (reason) => REASON_LABEL_MAP[reason] || REASON_LABEL_MAP["low-confidence"];

const reasonChipColor = (reason) =>
  REASON_CHIP_COLOR_MAP[reason] || REASON_CHIP_COLOR_MAP["low-confidence"];

// A confirmed non-spend-based classification (flights, waste, ...) has no
// naicsTitle at all -- useOnVendorSave only ever sets NAICS fields for the
// spend-based category. Used anywhere a naicsTitle might render or be
// searched against, so one of these conflicts is never blank/unfindable.
const formatCategoryLabel = (category) =>
  (category || "")
    .split("-")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const DELTA_NEGLIGIBLE_THRESHOLD = 0.05;

const calculateMergeDelta = (secondary, primary) => {
  if (!secondary || !primary || secondary.id === primary.id) {
    return { delta: 0, isKnown: true };
  }
  const secondaryEmissions = secondary.tonsCo2e || 0;
  const secondaryIntensity = secondary.tonsCo2ePerDollar || 0;

  if (!secondaryEmissions) {
    return { delta: 0, isKnown: true };
  }

  if (!secondaryIntensity) {
    return { delta: 0, isKnown: false };
  }

  const projectedSpend = secondaryEmissions / secondaryIntensity;
  const newEmissions = projectedSpend * (primary.tonsCo2ePerDollar || 0);
  return { delta: newEmissions - secondaryEmissions, isKnown: true };
};

const sumMergeDeltas = (deltas) =>
  deltas.reduce(
    (acc, { delta, isKnown }) => ({
      delta: acc.delta + delta,
      isKnown: acc.isKnown && isKnown
    }),
    { delta: 0, isKnown: true }
  );

const formatEmissionsDelta = ({ delta, isKnown }) => {
  if (!isKnown) {
    return {
      label: "Impact unknown — some vendors missing intensity",
      color: "textSecondary"
    };
  }
  if (Math.abs(delta) < DELTA_NEGLIGIBLE_THRESHOLD) {
    return { label: "No emissions change", color: "textSecondary" };
  }
  if (delta < 0) {
    return {
      label: `Saves ${formatDecimal(Math.abs(delta))} tCO2e`,
      color: "secondary"
    };
  }
  return {
    label: `Adds ${formatDecimal(delta)} tCO2e`,
    color: "error"
  };
};

const IMPACT_EMISSIONS_HIGH_THRESHOLD = 10;
const IMPACT_EMISSIONS_MEDIUM_THRESHOLD = 3;
const IMPACT_TRANSACTIONS_HIGH_THRESHOLD = 60;
const IMPACT_TRANSACTIONS_MEDIUM_THRESHOLD = 20;

const clusterTotalEmissions = (cluster) =>
  cluster.vendors.reduce((acc, v) => acc + (v.tonsCo2e || 0), 0);

const clusterTotalTransactions = (cluster) =>
  cluster.vendors.reduce((acc, v) => acc + (v.transactionCount || 0), 0);

const clusterImpactTier = (cluster) => {
  const totalEmissions = clusterTotalEmissions(cluster);
  const totalTransactions = clusterTotalTransactions(cluster);
  if (
    totalEmissions > IMPACT_EMISSIONS_HIGH_THRESHOLD ||
    totalTransactions > IMPACT_TRANSACTIONS_HIGH_THRESHOLD
  ) {
    return "high";
  }
  if (
    totalEmissions > IMPACT_EMISSIONS_MEDIUM_THRESHOLD ||
    totalTransactions > IMPACT_TRANSACTIONS_MEDIUM_THRESHOLD
  ) {
    return "medium";
  }
  return "low";
};

const impactChipColor = (cluster) =>
  IMPACT_CHIP_COLOR_MAP[clusterImpactTier(cluster)];

const buildHighImpactTooltip = (totalTransactions, totalEmissions, txnText, emissionsText) => {
  const transactionsHigh = totalTransactions > IMPACT_TRANSACTIONS_HIGH_THRESHOLD;
  const emissionsHigh = totalEmissions > IMPACT_EMISSIONS_HIGH_THRESHOLD;
  if (transactionsHigh && emissionsHigh) {
    return `High impact because of high transaction volume AND high emissions (${txnText}, ${emissionsText}).`;
  }
  if (transactionsHigh) {
    return `High impact because of high transaction volume (${txnText}, ${emissionsText} total).`;
  }
  return `High impact because of high emissions (${emissionsText} across ${txnText}).`;
};

const buildMediumImpactTooltip = (totalTransactions, totalEmissions, txnText, emissionsText) => {
  const transactionsMedium = totalTransactions > IMPACT_TRANSACTIONS_MEDIUM_THRESHOLD;
  const emissionsMedium = totalEmissions > IMPACT_EMISSIONS_MEDIUM_THRESHOLD;
  if (transactionsMedium && emissionsMedium) {
    return `Medium impact because of moderate transaction volume AND moderate emissions (${txnText}, ${emissionsText}).`;
  }
  if (transactionsMedium) {
    return `Medium impact because of moderate transaction volume (${txnText}, ${emissionsText} total).`;
  }
  return `Medium impact because of moderate emissions (${emissionsText} across ${txnText}).`;
};

const buildImpactTooltip = (cluster) => {
  const tier = clusterImpactTier(cluster);
  const totalEmissions = clusterTotalEmissions(cluster);
  const totalTransactions = clusterTotalTransactions(cluster);
  const txnText = `${totalTransactions} transaction${totalTransactions === 1 ? "" : "s"}`;
  const emissionsText = `${formatDecimal(totalEmissions)} tCO2e`;

  if (tier === "high") {
    return buildHighImpactTooltip(totalTransactions, totalEmissions, txnText, emissionsText);
  }
  if (tier === "medium") {
    return buildMediumImpactTooltip(totalTransactions, totalEmissions, txnText, emissionsText);
  }
  return `Low impact — only ${txnText} and ${emissionsText} across these vendors. Merging is unlikely to materially change your numbers.`;
};

export {
  confidenceTier,
  confidenceLabel,
  confidenceChipColor,
  matchBasisLabel,
  TIER_LABEL_MAP,
  MATCH_LABEL_MAP,
  IMPACT_LABEL_MAP,
  reasonLabel,
  reasonChipColor,
  formatCategoryLabel,
  calculateMergeDelta,
  sumMergeDeltas,
  formatEmissionsDelta,
  clusterImpactTier,
  clusterTotalEmissions,
  clusterTotalTransactions,
  impactChipColor,
  buildImpactTooltip
};
